import { DateTime, IANAZone } from 'luxon'
import { MY_PGA_COM_HOST } from 'env'

export const OFFERING_KIND = {
  BOOKING_TYPE: 'BOOKING_TYPE',
  LESSON_PACK: 'LESSON_PACK',
  VIRTUAL_COACHING: 'VIRTUAL_COACHING',
}

export const VIRTUAL_COACHING_CATEGORY = 'SWING_EVALUATION'

export const getOfferingKind = bookingType => {
  if (bookingType?.offeringKind === OFFERING_KIND.VIRTUAL_COACHING) {
    return OFFERING_KIND.VIRTUAL_COACHING
  }
  if (bookingType?.lessonPacks?.length > 0) {
    return OFFERING_KIND.LESSON_PACK
  }
  return OFFERING_KIND.BOOKING_TYPE
}

export const isVirtualCoaching = bookingType =>
  getOfferingKind(bookingType) === OFFERING_KIND.VIRTUAL_COACHING

// Golfers buy a virtual offering at its own checkout, not through the
// scheduler, so its share link differs from other booking types.
export const formatVirtualCoachingCheckoutUrl = lessonType => {
  const idOrSlug =
    lessonType?.coach?.coachProfile?.slug || lessonType?.coach?.externalId
  if (!idOrSlug || !lessonType?.id) return null
  return `${MY_PGA_COM_HOST}/coach/${idOrSlug}/purchase/virtual-coaching/${lessonType.id}`
}

export const TURNAROUND_HOURS_OPTIONS = [
  { label: '24 hours', value: 24 },
  { label: '48 hours', value: 48 },
  { label: '72 hours', value: 72 },
  { label: '1 week', value: 168 },
]

export const DEFAULT_TURNAROUND_HOURS = 72

export const PURCHASE_LIMIT_PERIOD_OPTIONS = [
  { label: 'per week', value: 'WEEK' },
  { label: 'per month', value: 'MONTH' },
]

// `expiresAt` is the last moment the offering can be bought; null means it is
// always available. Shown in the viewer's local time zone. Once it has passed,
// sales have ended.
export const formatExpiresAt = expiresAt => {
  const date = expiresAt ? DateTime.fromISO(expiresAt) : null
  if (!date?.isValid) return 'No expiration'
  const label = date.toLocaleString(DateTime.DATE_MED)
  return date < DateTime.local()
    ? `Sales ended ${label}`
    : `Available until ${label}`
}

const toZone = timezone =>
  timezone && IANAZone.isValidZone(timezone) ? timezone : undefined

// The facility's time zone when it is known and valid, otherwise the browser's
// (`undefined` makes Luxon use its default zone).
export const getOfferingZone = (facilities, locationId) => {
  const facility = facilities.find(({ id }) => id === locationId)
  return toZone(facility?.timezone)
}

// The date input holds a calendar day in the browser's zone. `expiresAt` is the
// last moment of that day in the offering's zone, not the browser's.
export const endOfDayInZone = (date, timezone) =>
  DateTime.fromObject(
    {
      year: date.year,
      month: date.month,
      day: date.day,
      hour: 23,
      minute: 59,
      second: 59,
      millisecond: 999,
    },
    { zone: toZone(timezone) },
  )

// The inverse: the calendar day `expiresAt` falls on in the offering's zone,
// as a browser-zone date for the date input, so the day does not shift when
// the coach's browser is in another zone.
export const expiresAtToDate = (expiresAt, timezone) => {
  const zoned = DateTime.fromISO(expiresAt, { zone: toZone(timezone) })
  return zoned.isValid
    ? DateTime.local(zoned.year, zoned.month, zoned.day)
    : null
}

export const formatTurnaround = hours => {
  const option = TURNAROUND_HOURS_OPTIONS.find(opt => opt.value === hours)
  if (option) return option.label
  return hours ? `${hours} hours` : null
}

// The description editor imports markdown with `preserveNewlines`, which turns
// every blank line into an empty block. Headings are followed directly by
// their list so no empty block sits between them.
export const VIRTUAL_COACHING_TEMPLATE = {
  title: 'Virtual Swing Analysis',
  description:
    "Send me a video of your swing and I'll review it and send back personalized video feedback and a simple plan to improve.\n\n**What to send**\n- A face-on video and a down-the-line video\n- One full swing per angle, about 15 seconds, from address through finish\n- Camera at hip height, with your full body and the club in frame\n\n**What you'll get**\n- A recorded analysis with a focus on the one thing that will help most\n- A clear next step to practice",
  turnaroundHours: DEFAULT_TURNAROUND_HOURS,
}

export const VIRTUAL_COACHING_LEGAL_CAPTION =
  'Virtual coaching is offered to coaches as a convenience. It is the individual coach’s responsibility to set clear expectations for the instruction included and the turnaround time.'
